import axios from 'axios';
import { createSupabaseClient } from './supabase';
import { Models, SubscriptionPlans } from '@/types';
import {
  UserProfile,
  UpdateUserProfileRequest,
  UpdateUserProfileResponse,
  UpdateUserSettingsRequest,
  UpdateUserSettingsResponse,
  PublicUserProfile,
  UsernameCheckResponse,
  AvatarUploadResponse,
  AvatarUrlResponse,
  DeleteProfileResponse,
} from '@/types/user';
import {
  ListOrganizationsResponse,
  OrganizationCreateRequest,
  OrganizationCreateResponse,
  OrganizationDetails,
  OrganizationMembersResponse,
} from '@/types/organizations';
import {
  IntegrationsResponse,
  JiraStatusResponse,
  JiraAuthResponse,
  JiraDisconnectResponse,
  JiraSyncStatusResponse,
  GitHubAuthResponse,
  GitHubStatusResponse,
  GitHubDisconnectResponse,
  SlackAuthResponse,
  SlackStatusResponse,
  SlackDisconnectResponse,
  GoogleAuthResponse,
  GoogleDisconnectResponse,
  LinearAuthResponse,
  LinearStatusResponse,
  LinearDisconnectResponse
} from '@/types/integrations';
import {
  ProjectContextInstruction,
  ProjectContextInstructionResponse,
  ProjectInstructionRequest,
} from '@/types/ProjectContextInstructions';
import {
  FileMetadata,
  ProjectContextFile,
  ProjectContextFileRequest,
  ProjectContextFileResponse,
} from '@/types/ProjectContextFile';
import {
  SettingsResponse,
  UpdateSettingsRequest,
  InvoicesResponse,
  InvoicesFilters,
  SubscriptionResponse,
  SubscriptionCheckResponse,
} from '@/types/settings';
import {
  FeedbackSubmit,
  FeedbackSubmitResponse,
  FeedbackStatusResponse,
} from '@/types/FeedbackSubmitTypes';
//import { disconnect } from 'process';

// Configuración del cliente para el backend
const backendAPI = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080',
  timeout: 30000, // 30 segundos para requests de AI
});

// Interceptor para agregar JWT token automáticamente
backendAPI.interceptors.request.use(
  async (config) => {
    const supabase = createSupabaseClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }

    // Detectar FormData de forma robusta para no fijar Content-Type incorrecto
    const isProbablyFormData = (() => {
      const d: any = (config as any).data;
      if (!d) return false;
      if (typeof FormData !== 'undefined' && d instanceof FormData) return true;
      // Heurísticas adicionales por diferencias de contexto
      const tag = d?.[Symbol.toStringTag];
      if (tag && String(tag) === 'FormData') return true;
      if (typeof d.append === 'function' && typeof d.get === 'function') return true;
      return false;
    })();

    if (isProbablyFormData) {
      // Dejar que el navegador gestione el boundary correctamente
      if (config.headers && 'Content-Type' in config.headers) {
        delete (config.headers as any)['Content-Type'];
      }
    } else {
      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de respuesta para capturar errores detallados
backendAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      headers: error.config?.headers,
    });
    return Promise.reject(error);
  }
);

// Tipos para las APIs
interface ChatRequest {
  chat_id?: string;
  context?: string;
  model?: string;
  project_uuids: string[];
  question: string;
  role: string;
}

interface ChatResponse {
  id: string;
  chat_id: string;
  question: string;
  answer: string;
  user_id?: string;
  role?: string;
  created_at?: string;
  updated_at?: string;
  sources?: string[];
  confidence?: number;
  duration?: number;
  metadata?: {
    total_tokens: number;
    prompt_tokens: number;
    completion_tokens: number;
    estimated_cost_usd: number;
    model: string;
  };
  tokens_used?: number;
  cost_usd?: number;
}

// Interfaces moved to types/integrations.ts

interface Project {
  id: string;
  key: string;
  name: string;
  project_type: string;
  description?: string;
  lead?: string;
  url?: string;
  created_at: string;
  updated_at: string;
}

export interface ChatsParams {
  search?: string;
  project_ids?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
  order_by?: 'created_at' | 'updated_at' | 'message_count' | 'title';
  order_dir?: 'desc' | 'asc';
}

export interface ChatsResponse {
  chats: Chat[];
  total_count: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface Chat {
  chat_id: string;
  session_id: string;
  title: string;
  last_message: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  project_ids: string[];
}

export interface DeleteChatResponse {
  chat_id: string;
  deletion_id: string;
  message: string;
  requested_at: string;
  status: string;
  success: boolean;
}

interface OverviewResponse {
  plan: {
    name: string;
    status: string;
    description: string;
    period_end: string;
  };
  usage: {
    spent_usd: number;
    limit_usd: number;
    usage_percent: number;
    requests_used: number;
    guaranteed_requests: number;
  };
  actions: {
    can_edit_limit: boolean;
    manage_subscription_url?: string;
    edit_limit_url?: string;
  };
}

interface DailyAnalyticsResponse {
  period: {
    start: string;
    end: string;
    range: string;
  };
  metrics: {
    total_requests: number;
    total_tokens: number;
    total_cost_usd: number;
  };
  daily_data: Array<{
    date: string;
    requests: number;
    tokens: number;
    cost_usd: number;
  }>;
}

interface TeamStatusResponse {
  team_enabled: boolean;
  current_members: number;
  max_members: number | null;
  pending_invitations: number;
  invite_url: string;
}

// Google types moved to types/integrations.ts

// Settings interfaces are now imported from types/settings.ts

// API endpoints organizados por módulo
export const api = {
  // AI Chat endpoints
  ai: {
    // Health check del sistema AI
    health: async () => {
      const response = await backendAPI.get('/api/ai/health');
      return response.data;
    },

    // Chat principal con AI
    chat: async (request: ChatRequest): Promise<ChatResponse> => {
      const response = await backendAPI.post('/api/ai/chat', request);
      return response.data;
    },

    chats: async (params: ChatsParams): Promise<ChatsResponse> => {
      const response = await backendAPI.get('/api/ai/chats', {
        params,
      });
      return response.data;
    },

    deleteChat: async (chatId: string): Promise<DeleteChatResponse> => {
      const response = await backendAPI.delete(`/api/ai/chats/${chatId}`);
      return response.data;
    },

    modelsAvailable: async (): Promise<Models> => {
      const response = await backendAPI.get('/api/ai/models/available');
      return response.data;
    },

    updateSelectedModel: async (modelName: string): Promise<void> => {
      const response = await backendAPI.put('/api/ai/models/preference', {
        preferred_model: modelName,
      });
      return response.data;
    },
  },

  // Projects
  projects: async (): Promise<any> => {
    const response = await backendAPI.get('/api/projects');
    return response.data;
  },

  projectContext: {
    getInstructionTypes: async (): Promise<any> => {
      const response = await backendAPI.get('/api/projects/instruction-types');
      return response.data;
    },
    getProjectContext: async (projectId: string): Promise<any> => {
      const response = await backendAPI.get(
        `/api/projects/${projectId}/context`
      );
      return response.data;
    },

    // Project Files
    getProjectFiles: async (
      projectId: string
    ): Promise<ProjectContextFileResponse> => {
      const response = await backendAPI.get(`/api/projects/${projectId}/files`);
      return response.data;
    },
    createProjectFile: async (
      projectId: string,
      fileReq: ProjectContextFileRequest
    ): Promise<ProjectContextFile> => {
      if (!fileReq.file) {
        throw new Error('No file provided');
      }

      const formData = new FormData();
      formData.append('file', fileReq.file);
      formData.append('file_type', fileReq.file_type);
      if (fileReq.description) {
        formData.append('description', fileReq.description);
      }

      const response = await backendAPI.post(
        `/api/projects/${projectId}/files`,
        formData
      );
      return response.data;
    },
    updateProjectFileMetadata: async (
      projectId: string,
      fileId: string,
      metadata: FileMetadata
    ): Promise<ProjectContextFile> => {
      const response = await backendAPI.put(
        `/api/projects/${projectId}/files/${fileId}/metadata`,
        metadata
      );
      return response.data;
    },
    deleteProjectFile: async (
      projectId: string,
      fileId: string
    ): Promise<any> => {
      const response = await backendAPI.delete(
        `/api/projects/${projectId}/files/${fileId}`
      );
      return response.data;
    },
    getProjectFileContent: async (
      projectId: string,
      fileId: string
    ): Promise<any> => {
      const response = await backendAPI.get(
        `/api/projects/${projectId}/files/${fileId}/content`
      );
      return response.data;
    },

    // Project Instructions
    getProjectInstructions: async (
      projectId: string
    ): Promise<ProjectContextInstructionResponse> => {
      const response = await backendAPI.get(
        `/api/projects/${projectId}/instructions`
      );
      return response.data;
    },
    createProjectInstructions: async (
      projectId: string,
      instructions: ProjectInstructionRequest
    ): Promise<ProjectContextInstruction> => {
      const response = await backendAPI.post(
        `/api/projects/${projectId}/instructions`,
        instructions
      );
      return response.data;
    },
    updateProjectInstructions: async (
      projectId: string,
      instructionId: string,
      instructions: any
    ): Promise<any> => {
      const response = await backendAPI.put(
        `/api/projects/${projectId}/instructions/${instructionId}`,
        instructions
      );
      return response.data;
    },
    deleteProjectInstructions: async (
      projectId: string,
      instructionId: string
    ): Promise<any> => {
      const response = await backendAPI.delete(
        `/api/projects/${projectId}/instructions/${instructionId}`
      );
      return response.data;
    },
  },

  // Roles
  roles: async (): Promise<any> => {
    const response = await backendAPI.get('/api/ai/roles');
    return response.data;
  },

  updateUserRole: async (role: string): Promise<void> => {
    const response = await backendAPI.put('/api/ai/roles', { role });
    return response.data;
  },

  plans: {
    get: async (): Promise<SubscriptionPlans> => {
      const response = await backendAPI.get('/api/subscription/plans');
      return response.data;
    },
    upgrade: async (planType: string): Promise<any> => {
      const response = await backendAPI.post(`/api/subscription/upgrade`, {
        plan_type: planType,
      });
      return response.data;
    },
  },

  // Integrations endpoints
  integrations: {
    // Obtener estado de todas las integraciones
    getAll: async (): Promise<IntegrationsResponse> => {
      const response = await backendAPI.get('/api/integrations');
      return response.data;
    },

    // Iniciar conexión OAuth con Jira  
    connectJira: async (): Promise<JiraAuthResponse> => {
      const response = await backendAPI.get('/auth/jira');
      return response.data;
    },

    // Obtener estado de la conexión
    getJiraStatus: async (): Promise<JiraStatusResponse> => {
      const response = await backendAPI.get('/api/jira/status');
      return response.data;
    },

    // Desconectar Jira
    disconnectJira: async (): Promise<JiraDisconnectResponse> => {
      const response = await backendAPI.delete('/api/jira/disconnect');
      return response.data;
    },

    // Obtener estado de la sincronización de Jira
    getJiraSyncStatus: async (): Promise<JiraSyncStatusResponse> => {
      const response = await backendAPI.get('/api/jira/sync-status');
      return response.data;
    },

    // Iniciar la autenticación con GitHub
    connectGitHub: async (): Promise<GitHubAuthResponse> => {
      const response = await backendAPI.get('/auth/github');
      return response.data;
    },

    // Obtener el estado de la conexión con GitHub
    getGitHubStatus: async (): Promise<GitHubStatusResponse> => {
      const response = await backendAPI.get('/api/github/status');
      return response.data;
    },

    // Desconectar de GitHub
    disconnectGitHub: async (): Promise<GitHubDisconnectResponse> => {
      const response = await backendAPI.delete('/api/github/disconnect');
      return response.data;
    },

    // Iniciar la autenticación con Slack
    connectSlack: async (): Promise<SlackAuthResponse> => {
      const response = await backendAPI.get('/auth/slack');
      return response.data;
    },

    // Obtener el estado de la conexión con Slack
    getSlackStatus: async (): Promise<SlackStatusResponse> => {
      const response = await backendAPI.get('/api/slack/status');
      return response.data;
    },

    // Desconectar de Slack
    disconnectSlack: async (): Promise<SlackDisconnectResponse> => {
      const response = await backendAPI.delete('/api/slack/disconnect');
      return response.data;
    },
    // Iniciar la autenticación con Google
    connectGoogle: async (): Promise<GoogleAuthResponse> => {
      const { data } = await backendAPI.get('/auth/google-apis');
      return data;
    },

    // Desconectar de Google
    disconnectGoogle: async (): Promise<GoogleDisconnectResponse> => {
      const { data } = await backendAPI.delete('/api/google/disconnect');
      return data;
    }
  },

  // Linear endpoints
  linear: {
    // Iniciar autenticación con Linear
    connect: async (): Promise<LinearAuthResponse> => {
      const response = await backendAPI.get('/auth/linear');
      return response.data;
    },
    // Estado de conexión
    status: async (): Promise<LinearStatusResponse> => {
      const response = await backendAPI.get('/api/linear/status');
      return response.data;
    },
    // Desconectar Linear (según especificación, POST)
    disconnect: async (): Promise<LinearDisconnectResponse> => {
      const response = await backendAPI.post('/api/linear/disconnect');
      return response.data;
    },
  },

  // User-related endpoints
  user: {
    // Obtener perfil del usuario autenticado
    getProfile: async (): Promise<UserProfile> => {
      const response = await backendAPI.get('/api/user/profile');
      return response.data;
    },

    // Actualizar campos del perfil del usuario
    updateProfile: async (
      updates: UpdateUserProfileRequest
    ): Promise<UpdateUserProfileResponse> => {
      const response = await backendAPI.patch('/api/user/profile', updates);
      return response.data;
    },

    // Actualizar settings (preferencias, notificaciones, privacidad)
    updateSettings: async (
      updates: UpdateUserSettingsRequest
    ): Promise<UpdateUserSettingsResponse> => {
      const response = await backendAPI.patch('/api/user/settings', updates);
      return response.data;
    },

    // Perfil público por username
    getPublicProfile: async (
      username: string
    ): Promise<PublicUserProfile> => {
      const response = await backendAPI.get(`/api/user/public/${username}`);
      return response.data;
    },

    // Verificar disponibilidad de username
    checkUsername: async (
      username: string
    ): Promise<UsernameCheckResponse> => {
      const response = await backendAPI.get('/api/user/check-username', {
        params: { username },
      });
      return response.data;
    },

    // Subir avatar (multipart/form-data)
    uploadAvatar: async (file: File): Promise<AvatarUploadResponse> => {
      const form = new FormData();
      form.append('avatar', file, file.name);

      // Usar fetch para evitar que Axios altere headers/transformations
      const supabase = createSupabaseClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const base = backendAPI.defaults.baseURL || (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080');

      const res = await fetch(`${base}/api/user/avatar`, {
        method: 'POST',
        headers: {
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {}),
          // No establecer Content-Type: el navegador añade el boundary
        } as any,
        body: form,
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        let errJson: any = {};
        try { errJson = JSON.parse(errText); } catch { }
        throw { response: { status: res.status, data: errJson || errText } };
      }
      const data = (await res.json()) as AvatarUploadResponse;
      return data;
    },

    // Obtener URL firmada del avatar
    getAvatarUrl: async (
      expiresInSeconds?: number
    ): Promise<AvatarUrlResponse> => {
      const response = await backendAPI.get('/api/user/avatar-url', {
        params: expiresInSeconds ? { expires_in: expiresInSeconds } : undefined,
      });
      return response.data;
    },

    // Importar avatar desde una URL de proveedor (e.g., GitHub/Google)
    // Asunción: backend expone POST /api/user/avatar/import { source_url }
    importAvatarFromUrl: async (sourceUrl: string): Promise<{ success: boolean; message?: string; avatar_url?: string }> => {
      const response = await backendAPI.post('/api/user/avatar/import', {
        source_url: sourceUrl,
      });
      return response.data;
    },

    // Eliminar cuenta de usuario
    deleteProfile: async (): Promise<DeleteProfileResponse> => {
      const response = await backendAPI.delete('/api/user/profile');
      return response.data;
    },
  },

  // Organizations endpoints
  organizations: {
    // Obtener todas las organizaciones del usuario
    list: async (): Promise<ListOrganizationsResponse> => {
      const response = await backendAPI.get('/api/organizations');
      return response.data;
    },

    // Obtener detalles de una organización por ID
    getById: async (id: string): Promise<OrganizationDetails> => {
      const response = await backendAPI.get(`/api/organizations/${id}`);
      return response.data;
    },

    // Crear una organización
    create: async (
      payload: OrganizationCreateRequest
    ): Promise<OrganizationCreateResponse> => {
      const response = await backendAPI.post('/api/organizations', payload);
      return response.data;
    },

    // Obtener miembros de una organización
    getMembers: async (
      id: string
    ): Promise<OrganizationMembersResponse> => {
      const response = await backendAPI.get(
        `/api/organizations/${id}/members`
      );
      return response.data;
    },

    // Invitar miembro a una organización
    invite: async (
      orgId: string,
      payload: { email: string; role: 'member' | 'administrator'; message?: string }
    ): Promise<{ success: boolean; invitation_id?: string; message?: string }> => {
      const response = await backendAPI.post(
        `/api/organizations/${orgId}/invite`,
        payload
      );
      return response.data;
    },
  },

  // Health check general
  health: async () => {
    const response = await backendAPI.get('/health');
    return response.data;
  },

  // Settings api endpoints
  settings: {
    // Obtener configuración completa de settings
    get: async (): Promise<SettingsResponse> => {
      const response = await backendAPI.get('/api/settings');
      return response.data;
    },

    // Actualizar configuración de settings
    update: async (updates: UpdateSettingsRequest) => {
      const response = await backendAPI.put('/api/settings', updates);
      return response.data;
    },

    // Obtener facturas de billing
    getInvoices: async (
      filters: InvoicesFilters = {}
    ): Promise<InvoicesResponse> => {
      const params = new URLSearchParams();

      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.year) params.append('year', filters.year.toString());
      if (filters.month) params.append('month', filters.month.toString());

      const queryString = params.toString();
      const url = `/api/billing/invoices${queryString ? `?${queryString}` : ''
        }`;

      const response = await backendAPI.get(url);
      return response.data;
    },

    // Obtener información de suscripción
    getSubscription: async (): Promise<SubscriptionResponse> => {
      const response = await backendAPI.get('/api/billing/subscription');
      return response.data;
    },

    // Obtener información de suscripción para upgrade modal (solo para verificar plan actual)
    getCurrentSubscription: async (): Promise<SubscriptionCheckResponse> => {
      const response = await backendAPI.get('/api/subscription');
      return response.data;
    },

    overview: async (): Promise<OverviewResponse> => {
      const response = await backendAPI.get('/api/dashboard/overview');
      return response.data;
    },
    dailyAnalytics: async (
      range: string = '7d'
    ): Promise<DailyAnalyticsResponse> => {
      const response = await backendAPI.get(
        `/api/dashboard/analytics/daily?range=${range}`
      );
      return response.data;
    },
    teamStatus: async (): Promise<TeamStatusResponse> => {
      const response = await backendAPI.get('/api/dashboard/team/status');
      return response.data;
    },
  },

  // Usage analytics endpoints
  usage: {
    // Obtener resumen de uso
    summary: async (period: string = 'current') => {
      const response = await backendAPI.get('/api/usage/summary', {
        params: { period },
      });
      return response.data;
    },

    // Obtener eventos de uso con paginación
    events: async (
      params: {
        period?: string;
        page?: number;
        limit?: number;
        start_date?: string;
        end_date?: string;
      } = {}
    ) => {
      // No forzar 'period' por defecto si se pasa un rango custom
      const base: any = {
        page: 1,
        limit: 50,
      };
      const isCustom = Boolean(params.start_date || params.end_date);
      if (!isCustom) {
        base.period = params.period ?? '7d';
      }
      const finalParams = { ...base, ...params };
      const response = await backendAPI.get('/api/usage/events', {
        params: finalParams,
      });
      return response.data;
    },

    // Export usage events as CSV. Ensures query ends with `&export=csv`.
    exportCsv: async (
      params: {
        period?: string;
        start_date?: string;
        end_date?: string;
      } = {}
    ): Promise<Blob> => {
      const search = new URLSearchParams();
      if (params.period) search.append('period', params.period);
      if (params.start_date) search.append('start_date', params.start_date);
      if (params.end_date) search.append('end_date', params.end_date);
      const qs = search.toString();
      const url = `/api/usage/events${qs ? `?${qs}&export=csv` : `?export=csv`}`; // export=csv al final
      const response = await backendAPI.get(url, { responseType: 'blob' });
      return response.data as Blob;
    },
  },

  // Sessions management endpoints
  sessions: {
    // Obtener sesiones activas
    getActive: async () => {
      const response = await backendAPI.get('/api/settings/sessions');
      
      // Debug: inspeccionar respuesta HTTP raw
      console.log('🌐 [API] Raw HTTP response from /api/settings/sessions:', response.data);
      
      return response.data;
    },

    // Revocar una sesión específica
    revoke: async (sessionId: string) => {
      const response = await backendAPI.delete(
        `/api/settings/sessions/${sessionId}`
      );
      return response.data;
    },
  },

  // Account management endpoints
  account: {
    // Solicitar eliminación de cuenta
    requestDeletion: async (reason?: string) => {
      const response = await backendAPI.post('/api/settings/account/delete', {
        reason,
      });
      return response.data;
    },
  },

  feedback: {
    submit: async (data: FeedbackSubmit): Promise<FeedbackSubmitResponse> => {
      const response = await backendAPI.post('/api/feedback/submit', data);
      return response.data;
    },

    getStatus: async (): Promise<FeedbackStatusResponse> => {
      const response = await backendAPI.get('/api/feedback/status');
      return response.data;
    },
  },

  // Reports endpoints
  reports: {
    submit: async (data: {
      subject: string;
      category: 'UI/UX' | 'Security' | 'Data Error' | 'Integration' | 'Incident/Crash' | 'Slow Performance' | 'Suggestion/Feature';
      description: string;
      file?: File;
    }): Promise<{
      success: boolean;
      report_id: string;
      priority: 'low' | 'normal' | 'high' | 'critical';
      status: string;
      message: string;
    }> => {
      const formData = new FormData();
      formData.append('subject', data.subject);
      formData.append('category', data.category);
      formData.append('description', data.description);

      if (data.file) {
        formData.append('file', data.file);
      }

      const response = await backendAPI.post('/api/reports/submit', formData);
      return response.data;
    },
  },
};

// Helper function para manejar errores de API
export const handleApiError = (error: any) => {
  if (error.response) {
    // Error del servidor con respuesta
    const status = error.response.status;
    const message =
      error.response.data?.message ||
      error.response.data?.error ||
      'Unknown error';

    switch (status) {
      case 401:
        return 'Authentication required. Please log in.';
      case 403:
        return "You don't have permission to perform this action.";
      case 404:
        return 'The requested resource was not found.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Internal server error. Please try again.';
      default:
        return message;
    }
  } else if (error.request) {
    // Error de red
    return 'Network error. Please check your connection.';
  } else {
    // Error de configuración
    return 'Request configuration error.';
  }
};

export default api;
